<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { usePageStore } from '@voix/store/pageStore'
import Container from '../blocks/Container.vue'

defineProps({
  currentResort: {
    type: Object,
    default: () => { },
  },
  resorts: {
    type: Array,
    required: true,
  },
})
const pageStore = usePageStore()
const currentPage = pageStore.currentPage
const menuIsOpen = ref(false)

const enLink = computed(() => {
  if (currentPage?.language_code === 'en')
    return { path: currentPage.path, language_code: currentPage.language_code }

  return currentPage?.localizations.find((localization: any) => localization.language_code === 'en')
})

const esLink = computed(() => {
  if (currentPage?.language_code === 'es')
    return { path: currentPage.path, language_code: currentPage.language_code }

  return currentPage?.localizations.find((localization: any) => localization.language_code === 'es')
})

const resortDropdownOpen = ref(false)
function toggleResortDropdown() {
  resortDropdownOpen.value = !resortDropdownOpen.value
}

const languageDropdownOpen = ref(false)
const languageOptions = computed(() => [
  { code: 'en', label: 'English', path: enLink.value?.path },
  { code: 'es', label: 'Español', path: esLink.value?.path },
])

function toggleLanguageDropdown() {
  if (languageDropdownOpen.value) {
    closeLanguageDropdown()
  }
  else {
    languageDropdownOpen.value = true
  }
}

function closeLanguageDropdown() {
  languageDropdownOpen.value = false
}

function onLanguageSelect(option: any) {
  if (option.path) {
    window.location.href = option.path
  }
  closeLanguageDropdown()
}

const signInDropdownOpen = ref(false)

function closeSignInDropdown() {
  signInDropdownOpen.value = false
}

function handleClickOutside(event: MouseEvent) {
  const dropdown = document.getElementById('sign-in-dropdown')
  const button = document.getElementById('sign-in-dropdown-btn')
  if (dropdown && !dropdown.contains(event.target as Node) && button && !button.contains(event.target as Node)) {
    closeSignInDropdown()
  }
}

const navMode = ref('location')

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
</script>

<template>
  <div>
    <nav
      class="py-3 duration-300 ease-out !z-[9999]"
      :class="{
        'menu-open': menuIsOpen,
        'bg-white text-primary-copy-color shadow-xl': !resortDropdownOpen,
        'h-full bg-action-bg-color text-action-copy-color': resortDropdownOpen,
      }"
    >
      <Container
        v-if="resortDropdownOpen"
        class="relative z-50 items-center overflow-y-auto overflow-x-hidden"
      >
        <!-- Close Button -->
        <button
          class="absolute -top-2 right-2 sm:top-8 sm:right-8 flex items-center space-y-2"
          @click="toggleResortDropdown"
        >
          <span class="uppercase font-title pt-2">Close</span>
          <Icon
            name="heroicons:x-mark"
            class="w-8 h-8"
          />
        </button>

        <!-- Controls -->
        <div class="text-center mt-8">
          <div class="text-2xl font-bold font-title uppercase">
            {{ $t('find-a-resort') }}
          </div>
        </div>

        <!-- By Location  -->
        <div v-if="navMode === 'location'">
          <div class="mt-8 md:min-w-[730px]">
            <div class="flex justify-center">
              <div class="mx-4 sm:mx-8 w-1/2 text-right">
                <h6 class="font-title mb-4 text-sm text-white/80 underline underline-offset-4">
                  Dominican Republic
                </h6>
                <ul>
                  <template
                    v-for="(resort, key) in resorts"
                    :key="key"
                  >
                    <template v-if="resort.country === 'Dominican Republic'">
                      <li class="mb-2">
                        <a
                          class="text-white font-copy text-xs sm:text-base"
                          :href="resort.permalink"
                        >
                          {{ resort.name }}
                        </a>
                      </li>
                    </template>
                  </template>
                </ul>
                <h6 class="mt-8 font-title mb-4 text-sm text-white/80 underline underline-offset-4">
                  Jamaica
                </h6>
                <ul>
                  <template
                    v-for="(resort, key) in resorts"
                    :key="key"
                  >
                    <template v-if="resort.country === 'Jamaica'">
                      <li class="mb-2">
                        <a
                          class="text-white font-copy text-xs sm:text-base"
                          :href="resort.permalink"
                        >
                          {{ resort.name }}
                        </a>
                      </li>
                    </template>
                  </template>
                </ul>
              </div>
              <div class="mx-4 sm:mx-8 w-1/2 text-left">
                <h6 class="font-title mb-4 text-sm text-white/80 underline underline-offset-4">
                  Mexico
                </h6>
                <ul>
                  <template
                    v-for="(resort, key) in resorts"
                    :key="key"
                  >
                    <template v-if="resort.country === 'Mexico'">
                      <li class="mb-2">
                        <a
                          class="text-white font-copy text-xs sm:text-base"
                          :href="resort.permalink"
                        >
                          {{ resort.name }}
                        </a>
                      </li>
                    </template>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- By Brand  -->
        <div
          v-if="navMode === 'brand'"
          class=" mt-8 max-w-4xl"
        >
          <div class="grid grid-cols-2 md:grid-cols-3 w-full">
            <!-- Top Row -->
            <div
              class="flex items-center justify-center py-4 px-3 lg:px-12  border-b border-r border-white/30 hover:bg-white/10"
            >
              <a
                href="https://www.resortsbyhyatt.com/"
                class="zoom-emphasize"
                aria-label="Hyatt Ziva"
                target="_blank"
              >
                <LazyLogosHyattZiva
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div
              class="flex items-center justify-center py-4 px-3 lg:px-12  border-b border-r border-white/30 hover:bg-white/10"
            >
              <a
                href="https://www.resortsbyhyatt.com/"
                class="zoom-emphasize"
                aria-label="Hyatt Zilara"
                target="_blank"
              >
                <LazyLogosHyattZilara
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div
              class="flex items-center justify-center py-4 px-3 lg:px-12  border-b border-white/30 hover:bg-white/10"
            >
              <a
                href="https://www.playaresorts.com/turquoize-ziva-cancun/"
                class="zoom-emphasize"
                aria-label="Turquoize"
                target="_blank"
              >
                <LazyLogosTurquoize
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>

            <!-- Middle Row -->
            <div
              class="flex items-center justify-center py-4 px-5 lg:px-8  border-b border-r border-white/30 hover:bg-white/10"
            >
              <a
                href="https://hiltonbyplaya.com/"
                class="zoom-emphasize"
                aria-label="Hilton"
                target="_blank"
              >
                <LazyLogosHilton
                  class="max-h-[80px] w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div
              class="flex items-center justify-center py-4 px-3 lg:px-12 border-b border-r border-white/30 hover:bg-white/10"
            >
              <a
                href="https://sanctuarycapcana.com/"
                class="zoom-emphasize"
                aria-label="Sanctuary"
                target="_blank"
              >
                <LazyLogosSanctuary
                  class="h-fit w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div class="flex items-center justify-center py-4 px-3 lg:px-12 border-b border-white/30 hover:bg-white/10">
              <a
                href="https://kimptontresrios.com/"
                class="zoom-emphasize py-8"
                aria-label="Jewel Grande"
                target="_blank"
              >
                <LazyLogosKimpton
                  class="w-full text-white"
                  :fill="false"
                />
              </a>
            </div>

            <!-- Bottom Row -->
            <div class="flex items-center justify-center py-4 px-3 lg:px-12 border-r border-white/30 hover:bg-white/10">
              <a
                href="https://www.jewelgrande.com/"
                class="zoom-emphasize"
                aria-label="Jewel Grande"
                target="_blank"
              >
                <LazyLogosJewelGrande
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div class="flex items-center justify-center py-4 px-3 lg:px-12 border-r border-white/30 hover:bg-white/10">
              <a
                href="https://alltrabyplaya.com/"
                class="zoom-emphasize"
                aria-label="Wyndham Alltra"
                target="_blank"
              >
                <LazyLogosWyndham
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>
            <div class="flex items-center justify-center py-4 px-3 lg:px-12 border-white/30">
              <a
                href="https://seadustcancun.com/"
                class="zoom-emphasize"
                aria-label="Wyndham Alltra"
                target="_blank"
              >
                <LazyLogosSeadust
                  class="w-full"
                  :fill="false"
                />
              </a>
            </div>
          </div>
        </div>
      </Container>

      <div
        :class="{ 'bg-hyatt-dreams-primary': menuIsOpen }"
        class="h-full"
      >
        <Container class="z-50">
          <div class="font-sans flex items-center justify-between h-full px-4 xs:px-4 md:px-0">
            <div class="w-1/2 md:w-1/5 relative flex justify-start">
              <a
                href="/"
                class="block relative z-10"
              >
                <img
                  src="https://www.hyatt.com/hds/bookends/images/logos/_01/woh-horizontal_ol.svg"
                  alt="Hyatt Logo"
                  class="w-full h-[36px] object-contain"
                >
              </a>
            </div>

            <div class="flex items-center justify-end space-x-2">
              <div class="w-full flex items-center justify-end relative z-50">
                <button
                  class="flex min-w-[150px] items-center text-black text-xs font-light focus:outline-none px-2 py-1 rounded hover:bg-gray-100"
                  aria-haspopup="listbox"
                  :aria-expanded="resortDropdownOpen"
                  @click="toggleResortDropdown"
                >
                  <Icon
                    name="heroicons:globe-americas"
                    class="w-5 h-5 mr-1"
                  />
                  <span>{{ $t('find-a-resort') }}</span>
                  <Icon
                    name="heroicons:chevron-down-20-solid"
                    class="w-4 h-4 ml-1"
                  />
                </button>
              </div>
              <div class="hidden sm:block w-full flex items-center justify-end relative z-50">
                <button
                  class="flex items-center text-black text-xs font-light focus:outline-none px-2 py-1 rounded hover:bg-gray-100"
                  aria-haspopup="listbox"
                  :aria-expanded="languageDropdownOpen"
                  @click="toggleLanguageDropdown"
                >
                  <Icon
                    name="heroicons:language"
                    class="w-5 h-5 mr-1"
                  />
                  <span>Language</span>
                  <Icon
                    name="heroicons:chevron-down-20-solid"
                    class="w-4 h-4 ml-1"
                  />
                </button>
                <transition name="fade">
                  <ul
                    v-if="languageDropdownOpen"
                    class="absolute top-9 -left-16 mt-2 w-48 bg-white shadow-lg py-2 z-[9999] border border-gray-100"
                    @mouseleave="closeLanguageDropdown"
                  >
                    <li
                      v-for="option in languageOptions"
                      :key="option.code"
                      class="px-4 py-2 text-black text-sm hover:bg-gray-100 cursor-pointer"
                      :class="{ 'bg-gray-100 font-semibold': currentPage.language_code === option.code }"
                      @click="onLanguageSelect(option)"
                    >
                      {{ option.label }}
                    </li>
                  </ul>
                </transition>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </nav>
  </div>
</template>

<style scoped>
:global(body:has(.menu-open)) {
  overflow: hidden;
}

.hyatt-ziva-theme {
  .bg-action-bg-color {
    background-color: #d23476 !important;
  }
}

.hyatt-zilara-theme {
  .bg-action-bg-color {
    background-color: #1e1b57 !important;
  }
}

nav {
  @apply fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in;

  .x-container {
    @apply flex flex-col h-full;
  }

  &::before {
    @apply block fixed top-0 left-0 right-0 h-0 w-full bg-hyatt-dreams-primary z-[-1];
    content: '';
    background-image: url(/imgs/hyatt/illustrations/feather-bg.png);
  }

  &.menu-open {
    @apply bg-transparent h-screen pb-0;

    &::before {
      @apply h-screen;
      animation: NavHeightAnimated 0.3s ease-in forwards;
    }

    .menu {
      /* HIDE SCROLLBARS: START */
      -ms-overflow-style: none;
      /* Internet Explorer 10+ */
      scrollbar-width: none;

      /* Firefox */
      &::-webkit-scrollbar {
        display: none;
        /* Safari and Chrome */
      }

      /* HIDE SCROLLBARS: END */

      .accordion {
        @apply w-full;
      }

      .logos {
        @apply grid grid-cols-1 gap-10 xs:grid-cols-2 md:grid-cols-3 md:gap-24;

        span {
          @apply border border-white/30 hover:bg-white/10 block px-2 py-1 font-light uppercase text-xs opacity-60 mx-auto w-max mt-4;
        }
      }
    }
  }
}

@keyframes NavHeightAnimated {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
